import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Sparkles, Coins, Lock, ChevronUp, Check, Trophy } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";

/**
 * Artifacts — special paddles + balls with upgradable powers (5 tiers).
 *
 * Pilots buy each artifact for 300 ☾, then upgrade through L2..L5
 * with progressive costs (100 / 250 / 500 / 1000 / 2000 ☾). One paddle
 * + one ball can be equipped at a time. Powers are auto-applied in
 * non-competitive modes (campaign, endless, daily) — disabled in
 * tournaments + wagers to keep PvP fair.
 */
export default function Artifacts() {
  const [data, setData] = useState(null);  // { catalog, buy_cost, max_level, user }
  const [wallet, setWallet] = useState({ balance: 0, cap: 5000 });
  const [busyId, setBusyId] = useState(null);

  const refresh = async () => {
    try {
      const [a, w] = await Promise.all([
        api.get("/artifacts"),
        api.get("/moonstones/me"),
      ]);
      setData(a.data);
      setWallet({
        balance: w.data.balance ?? 0,
        cap: w.data.cap ?? 5000,
      });
    } catch {
      /* noop */
    }
  };

  // Stagger trophy unlocks so a cascade of N unlocks doesn't pile on the
  // success toast. Each carries an Equip action when the trophy ships a
  // reward skin (mirrors the Play.jsx onComplete pattern).
  const surfaceUnlockedToasts = (newly) => {
    if (!Array.isArray(newly) || newly.length === 0) return;
    newly.forEach((a, idx) => {
      setTimeout(() => {
        const tierColor =
          a.tier === "legendary" ? "#b026ff"
            : a.tier === "gold" ? "#ffd700"
              : a.tier === "silver" ? "#c0c0c0"
                : "#cd7f32";
        const equipAction = a.reward_skin
          ? {
              label: "Equip",
              onClick: () => {
                api
                  .post("/ball-skins/active", { skin_id: a.reward_skin })
                  .then(() => {
                    toast.success("Equipped!", {
                      description: `${a.reward_skin.replace(/-/g, " ")} is now your active comet.`,
                      icon: <Trophy className="w-4 h-4 text-[#ffd700]" />,
                      duration: 3500,
                    });
                  })
                  .catch(() => toast.error("Couldn't equip — try /skins"));
              },
            }
          : undefined;
        toast(`Trophy unlocked · ${a.name}`, {
          description: `${a.desc} · +${a.reward_score || 0} pts`,
          icon: <Trophy className="w-4 h-4" style={{ color: tierColor }} />,
          duration: equipAction ? 7500 : 5000,
          action: equipAction,
        });
      }, idx * 600);
    });
  };

  useEffect(() => {
    refresh();
  }, []);

  const buy = async (id) => {
    if (busyId) return;
    setBusyId(id);
    try {
      const r = await api.post("/artifacts/buy", { id });
      toast.success("Artifact unlocked", {
        description: `Equip it from this page · balance: ${r.data.balance} ☾`,
      });
      surfaceUnlockedToasts(r.data.newly_unlocked_achievements);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't buy artifact");
    } finally {
      setBusyId(null);
    }
  };

  const upgrade = async (id, currentLevel) => {
    if (busyId) return;
    setBusyId(id);
    try {
      const r = await api.post("/artifacts/upgrade", { id });
      toast.success(`Upgraded to L${r.data.new_level}`, {
        description: `Balance: ${r.data.balance} ☾`,
      });
      surfaceUnlockedToasts(r.data.newly_unlocked_achievements);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upgrade failed");
    } finally {
      setBusyId(null);
    }
  };

  const equip = async (id, type) => {
    if (busyId) return;
    setBusyId(id);
    try {
      await api.post("/artifacts/equip", { id, type });
      toast.success("Equipped");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Equip failed");
    } finally {
      setBusyId(null);
    }
  };

  const unequip = async (type) => {
    setBusyId(`unequip-${type}`);
    try {
      await api.post("/artifacts/equip", { id: null, type });
      toast.success(`Unequipped ${type}`);
      await refresh();
    } catch {
      /* noop */
    } finally {
      setBusyId(null);
    }
  };

  const paddles = useMemo(
    () => (data?.catalog || []).filter((a) => a.type === "paddle"),
    [data]
  );
  const balls = useMemo(
    () => (data?.catalog || []).filter((a) => a.type === "ball"),
    [data]
  );

  if (!data) {
    return (
      <div className="relative min-h-screen">
        <Navbar />
        <main className="max-w-5xl mx-auto px-6 pt-28 pb-20">
          <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/40">
            Loading artifacts…
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <Navbar />
      <main
        className="max-w-5xl mx-auto px-6 pt-28 pb-24"
        data-testid="artifacts-page"
      >
        {/* Header */}
        <div className="mb-10 flex flex-col sm:flex-row sm:items-end gap-3 sm:justify-between">
          <div>
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#b026ff]/85 uppercase mb-1">
              // ARTIFACTS · UPGRADABLE POWERS
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">
              Special Paddles & Balls
            </h1>
            <div className="font-mono-cosmic text-[11px] tracking-[0.15em] text-white/50 mt-2 max-w-2xl">
              Each artifact unlocks a unique power. Upgrade through 5 tiers
              for stronger effects. Auto-applies in Campaign, Endless, and
              Daily Run · disabled in Brackets and Wagers.
            </div>
          </div>
          <div
            className="border border-[#ffd700]/40 bg-black/40 px-4 py-2 self-start sm:self-end"
            data-testid="artifacts-wallet"
          >
            <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#ffd700]/70 mb-0.5">
              Wallet
            </div>
            <div className="flex items-baseline gap-2 font-mono-cosmic">
              <Coins className="w-3.5 h-3.5 text-[#ffd700]" />
              <span className="text-[#ffd700] text-lg tabular-nums">
                {wallet.balance.toLocaleString()}
              </span>
              <span className="text-white/35 text-[10px] tabular-nums">
                / {wallet.cap.toLocaleString()} ☾
              </span>
            </div>
          </div>
        </div>

        {/* Equipped slots */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
          <EquipSlot
            type="paddle"
            id={data.user.equipped_paddle}
            level={data.user.levels[data.user.equipped_paddle]}
            catalog={data.catalog}
            onUnequip={() => unequip("paddle")}
          />
          <EquipSlot
            type="ball"
            id={data.user.equipped_ball}
            level={data.user.levels[data.user.equipped_ball]}
            catalog={data.catalog}
            onUnequip={() => unequip("ball")}
          />
        </div>

        {/* Paddles section */}
        <Section title="Paddles" accent="#b026ff" testId="artifacts-paddles">
          {paddles.map((a) => (
            <ArtifactCard
              key={a.id}
              art={a}
              user={data.user}
              wallet={wallet}
              busy={busyId === a.id}
              onBuy={() => buy(a.id)}
              onUpgrade={(lvl) => upgrade(a.id, lvl)}
              onEquip={() => equip(a.id, a.type)}
            />
          ))}
        </Section>

        {/* Balls section */}
        <Section title="Balls" accent="#ffd700" testId="artifacts-balls">
          {balls.map((a) => (
            <ArtifactCard
              key={a.id}
              art={a}
              user={data.user}
              wallet={wallet}
              busy={busyId === a.id}
              onBuy={() => buy(a.id)}
              onUpgrade={(lvl) => upgrade(a.id, lvl)}
              onEquip={() => equip(a.id, a.type)}
            />
          ))}
        </Section>
      </main>
    </div>
  );
}

function Section({ title, accent, testId, children }) {
  return (
    <section className="mb-10" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-3 border-b border-white/10 pb-2">
        <h2
          className="font-heading text-2xl"
          style={{ color: accent }}
        >
          {title}
        </h2>
        <span className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/35">
          {String(children?.length || 0).padStart(2, "0")} ITEMS
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>
    </section>
  );
}

function EquipSlot({ type, id, level, catalog, onUnequip }) {
  const art = id ? catalog.find((a) => a.id === id) : null;
  return (
    <div
      className={`border ${art ? "border-[#28e070]/35" : "border-white/10"} bg-black/30 p-4 flex items-center gap-3`}
      data-testid={`artifacts-equipped-${type}`}
    >
      <div
        className={`w-12 h-12 flex items-center justify-center text-2xl border ${art ? "border-[#28e070]/55" : "border-white/15"}`}
      >
        {art ? art.icon : "—"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45">
          Equipped {type}
        </div>
        <div className="font-mono-cosmic text-sm text-white truncate">
          {art ? art.name : <span className="text-white/35">— None equipped</span>}
        </div>
        {art && (
          <div className="font-mono-cosmic text-[10px] text-[#28e070]/85 tabular-nums">
            Level {level || 1} · {art.tiers[Math.max(0, (level || 1) - 1)].label}
          </div>
        )}
      </div>
      {art && (
        <button
          onClick={onUnequip}
          className="px-2 py-1 border border-white/15 hover:border-white/40 font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/55 hover:text-white"
          data-testid={`artifacts-unequip-${type}-btn`}
        >
          Clear
        </button>
      )}
    </div>
  );
}

function ArtifactCard({ art, user, wallet, busy, onBuy, onUpgrade, onEquip }) {
  const owned = (user.owned || []).includes(art.id);
  const level = user.levels[art.id] || 0;
  const equipped =
    art.type === "paddle"
      ? user.equipped_paddle === art.id
      : user.equipped_ball === art.id;
  const maxLevel = art.max_level || (art.tiers?.length) || 5;
  const isMax = level >= maxLevel;
  const upgradeIdx = level - 1;
  const upgradeCost =
    !isMax && level > 0 ? art.cost_upgrade[upgradeIdx] : null;
  const buyAffordable = art.cost_buy != null && wallet.balance >= art.cost_buy;
  const upgradeAffordable =
    upgradeCost != null && wallet.balance >= upgradeCost;
  const isPrismatic = art.rarity === "prismatic";
  const accent = isPrismatic ? "#67e8f9" : (art.type === "paddle" ? "#b026ff" : "#ffd700");

  return (
    <div
      className={`relative border ${
        isPrismatic
          ? "border-[#67e8f9]/45"
          : owned
            ? "border-[#b026ff]/35"
            : "border-white/10"
      } bg-black/30 p-4 flex flex-col`}
      style={
        isPrismatic
          ? {
              boxShadow:
                "0 0 24px rgba(103,232,249,0.18), inset 0 0 12px rgba(176,38,255,0.10)",
              backgroundImage:
                "linear-gradient(135deg, rgba(103,232,249,0.04), rgba(176,38,255,0.04), rgba(255,61,240,0.04))",
            }
          : undefined
      }
      data-testid={`artifact-card-${art.id}`}
    >
      {isPrismatic && (
        <div
          className="absolute -top-2 right-3 px-1.5 py-0.5 font-mono-cosmic text-[8px] tracking-[0.25em] uppercase"
          style={{
            color: "#0a0716",
            background:
              "linear-gradient(90deg, #67e8f9, #b026ff, #ff3df0, #ffd700)",
          }}
          data-testid={`artifact-rarity-prismatic-${art.id}`}
        >
          ★ Prismatic
        </div>
      )}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-12 h-12 shrink-0 flex items-center justify-center text-2xl border border-white/15"
          style={{ color: accent }}
        >
          {art.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-white/45">
            {art.type}
          </div>
          <div className="font-heading text-lg text-white">{art.name}</div>
        </div>
        {equipped && (
          <span
            className="px-1.5 py-0.5 border border-[#28e070]/55 text-[#28e070] font-mono-cosmic text-[8px] tracking-[0.2em] uppercase flex items-center gap-1"
            data-testid={`artifact-equipped-badge-${art.id}`}
          >
            <Check className="w-2.5 h-2.5" /> Equipped
          </span>
        )}
      </div>
      <div className="font-mono-cosmic text-[11px] text-white/60 leading-relaxed mb-3 flex-1">
        {art.desc}
      </div>

      {/* Tier ladder — 5 cols for core, 6 cols for prismatic */}
      <div
        className={`grid gap-1 mb-3 ${maxLevel >= 6 ? "grid-cols-6" : "grid-cols-5"}`}
      >
        {art.tiers.map((t) => {
          const reached = level >= t.level;
          return (
            <div
              key={t.level}
              className={`text-center py-1 border font-mono-cosmic text-[9px] tabular-nums ${
                reached
                  ? `border-[${accent}]/50 text-white bg-white/5`
                  : "border-white/10 text-white/35"
              }`}
              style={
                reached
                  ? { borderColor: `${accent}88`, background: `${accent}11` }
                  : undefined
              }
              title={`Level ${t.level}: ${t.label}`}
              data-testid={`artifact-${art.id}-tier-${t.level}`}
            >
              <div>L{t.level}</div>
              <div className="text-[8px] opacity-75">{t.label}</div>
            </div>
          );
        })}
      </div>

      {/* CTA — buy / upgrade / equip */}
      {!owned ? (
        isPrismatic ? (
          <div
            className="w-full py-2 border border-[#67e8f9]/40 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-[#67e8f9] text-center"
            data-testid={`artifact-prismatic-locked-${art.id}`}
          >
            Boss Drop · Lvl {art.drop_level}
          </div>
        ) : (
          <button
            onClick={onBuy}
            disabled={!buyAffordable || busy}
            className="w-full py-2 border border-[#ffd700] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-[#ffd700] hover:bg-[#ffd700]/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            data-testid={`artifact-buy-${art.id}`}
          >
            {!buyAffordable ? (
              <>
                <Lock className="w-3 h-3" /> NEED {art.cost_buy} ☾
              </>
            ) : busy ? (
              "BUYING…"
            ) : (
              <>
                <Sparkles className="w-3 h-3" /> UNLOCK · {art.cost_buy} ☾
              </>
            )}
          </button>
        )
      ) : (
        <div className="flex gap-1.5">
          <button
            onClick={onEquip}
            disabled={equipped || busy}
            className={`flex-1 py-2 border font-mono-cosmic text-[10px] tracking-[0.25em] uppercase ${
              equipped
                ? "border-[#28e070]/35 text-[#28e070]/55 cursor-default"
                : "border-[#28e070] text-[#28e070] hover:bg-[#28e070]/10"
            } disabled:opacity-50`}
            data-testid={`artifact-equip-${art.id}`}
          >
            {equipped ? "EQUIPPED" : "EQUIP"}
          </button>
          <button
            onClick={() => onUpgrade(level)}
            disabled={isMax || !upgradeAffordable || busy}
            className="flex-1 py-2 border border-[#b026ff] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-[#b026ff] hover:bg-[#b026ff]/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            data-testid={`artifact-upgrade-${art.id}`}
          >
            {isMax ? (
              "MAX"
            ) : !upgradeAffordable ? (
              <>
                <Lock className="w-3 h-3" /> {upgradeCost} ☾
              </>
            ) : (
              <>
                <ChevronUp className="w-3 h-3" /> L{level + 1} · {upgradeCost} ☾
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
